import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

const VIEWPORT_PAD = 8;
const GAP = 10;
const HIDE_DELAY_MS = 120;
const DEFAULT_PREVIEW_WIDTH = 280;
const DEFAULT_PREVIEW_HEIGHT = 392;

function computePreviewPosition(
  trigger: DOMRect,
  panelW: number,
  panelH: number,
): { top: number; left: number } {
  const vw = globalThis.innerWidth;
  const vh = globalThis.innerHeight;

  const candidates = [
    { left: trigger.right + GAP, top: trigger.top + trigger.height / 2 - panelH / 2 },
    { left: trigger.left - GAP - panelW, top: trigger.top + trigger.height / 2 - panelH / 2 },
    { left: trigger.left + trigger.width / 2 - panelW / 2, top: trigger.bottom + GAP },
    { left: trigger.left + trigger.width / 2 - panelW / 2, top: trigger.top - GAP - panelH },
  ];

  const fits = (left: number, top: number) =>
    left >= VIEWPORT_PAD &&
    top >= VIEWPORT_PAD &&
    left + panelW <= vw - VIEWPORT_PAD &&
    top + panelH <= vh - VIEWPORT_PAD;

  for (const c of candidates) {
    if (fits(c.left, c.top)) return c;
  }

  const fallback = candidates[0]!;
  return {
    left: Math.max(VIEWPORT_PAD, Math.min(fallback.left, vw - panelW - VIEWPORT_PAD)),
    top: Math.max(VIEWPORT_PAD, Math.min(fallback.top, vh - panelH - VIEWPORT_PAD)),
  };
}

export function ImageHoverPreview({
  src,
  alt = "",
  children,
  previewWidth = DEFAULT_PREVIEW_WIDTH,
  previewHeight = DEFAULT_PREVIEW_HEIGHT,
}: Readonly<{
  src: string;
  alt?: string;
  children: ReactNode;
  /** Fixed preview panel width — used for viewport collision before the image loads. */
  previewWidth?: number;
  /** Fixed preview panel height — used for viewport collision before the image loads. */
  previewHeight?: number;
}>) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [visible, setVisible] = useState(false);

  const clearHideTimer = useCallback(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setPos(computePreviewPosition(rect, previewWidth, previewHeight));
    setVisible(true);
  }, [previewWidth, previewHeight]);

  const show = useCallback(() => {
    clearHideTimer();
    setVisible(false);
    setOpen(true);
  }, [clearHideTimer]);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimer.current = setTimeout(() => {
      setOpen(false);
      setVisible(false);
      setPos(null);
    }, HIDE_DELAY_MS);
  }, [clearHideTimer]);

  useEffect(() => {
    if (!open) return;
    reposition();
    const onMove = () => reposition();
    globalThis.addEventListener("scroll", onMove, true);
    globalThis.addEventListener("resize", onMove);
    return () => {
      globalThis.removeEventListener("scroll", onMove, true);
      globalThis.removeEventListener("resize", onMove);
    };
  }, [open, reposition]);

  useEffect(() => () => clearHideTimer(), [clearHideTimer]);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={scheduleHide}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-auto fixed z-[10000] flex items-center justify-center rounded-xl bg-surface p-2 shadow-xl ring-1 ring-white/10"
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              width: previewWidth,
              height: previewHeight,
              visibility: visible ? "visible" : "hidden",
            }}
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
          >
            <img src={src} alt={alt} className="max-h-full max-w-full object-contain" />
          </div>,
          document.body,
        )}
    </>
  );
}
